// What the child is shown once a training route is over: the modal's title, one row per
// task and (for the exam) the fault footer. Pure — no Phaser, no DOM — so `node --test`
// covers it; TrainingRouteScene hands the rows over and OutsideScene prints them.
//
// The rows describe what the RUN did (routeRules.mjs's task statuses); the counts and the
// verdict come from the SERVER's own scoring (driving.score_tasks), never from these rows.

const SEVERITY_PL = { minor: 'drobny', major: 'poważny' };

/** The mark at the end of a row: ✅, ❌ (with its weight), or „—" for nothing asked. */
export function taskMark({ status, severity } = {}) {
  if (status === 'ok') return '{check}';
  if (status === 'fail') {
    const label = SEVERITY_PL[severity];
    return label ? `{cross} (${label})` : '{cross}';
  }
  // Never resolved. A rule that carries no weight (a no-entry sign, a finish an exam ended
  // before) has nothing to say either way; anything else the server counts as driven cleanly.
  return severity === 'none' ? '—' : '{check}';
}

/**
 * One row per task, in driving order. `tasks` are `{icon, title, severity, status}` —
 * exactly the fields a run's tasks carry (see routeRules.mjs / lessonScript.mjs).
 */
export function summaryRows(tasks = []) {
  return tasks
    .filter((t) => t && t.title)
    .map((t) => `${t.icon ?? '•'} ${t.title} — ${taskMark(t)}`);
}

/**
 * The footer under the rows: the exam's fault line from the SERVER's counts; for a lesson
 * that did not pass, the bar it has to clear (every task ✅). A passed lesson has none.
 */
export function summaryFooter({ kind, passed, minor = 0, major = 0, maxMinor = 2 } = {}) {
  if (kind === 'exam') return `Błędy drobne: ${minor}/${maxMinor} · poważne: ${major}`;
  return passed ? null : 'Lekcja liczy się, gdy wszystkie zadania są {check} — spróbuj jeszcze raz.';
}

/** The summary modal's title. `n` is the lesson number (lessons only). */
export function summaryTitle({ kind, n, passed } = {}) {
  if (kind === 'exam') {
    return passed
      ? 'Egzamin zdany! {party} Masz prawo jazdy'
      : 'Egzamin oblany — następne podejście jutro';
  }
  return passed ? `Lekcja ${n} zaliczona {car}` : `Lekcja ${n} do powtórki`;
}

/** Title + body for the modal, ready to print. */
export function summaryBody(result = {}) {
  const rows = summaryRows(result.tasks);
  const footer = summaryFooter(result);
  const lines = rows.length ? rows : ['Trasa przejechana.'];
  return footer ? `${lines.join('\n')}\n\n${footer}` : lines.join('\n');
}
